import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { TranslateHTML } from 'core/translate';
import { SvgIcon } from 'core/icons';
import { combineClassNames } from '../../../core/css';
import { useModelInfo } from '../../common/ModelInfoContext';

const VehicleUtilityInfo = ({
  config,
  trim,
  driveType,
  bodyStyle,
  year,
  isRevealed,
  isSelectionRevealed,
  modifier = null,
}) => {
  const modelInfo = useModelInfo();
  const { country, language, isEvMode } = modelInfo;

  const isCanadaFrench = country === 'CA' && language === 'fr';

  if (isCanadaFrench) {
    return (
      <>
        <h1
          className={`utility-bar__title-content--revealed ${
            isRevealed ? '' : 'utility-bar__title-content--hidden'
          }`}>
          <div
            className={combineClassNames(
              'utility-bar__title',
              modifier ? `utility-bar__title--${modifier}` : ''
            )}>
            <TranslateHTML value="utility.bar.title.v3" bodyStyle={bodyStyle} />
          </div>
          <div
            className={combineClassNames(
              'utility-bar__sub-title',
              modifier ? `utility-bar__sub-title--${modifier}` : ''
            )}>
            {isSelectionRevealed ? (
              <>
                {config && !isEvMode ? (
                  <TranslateHTML
                    value="utility.bar.sub.title.with.config.v3"
                    config={config}
                    trim={trim}
                    driveType={driveType}
                    year={year}></TranslateHTML>
                ) : (
                  <TranslateHTML
                    value="utility.bar.sub.title.without.config.v3"
                    trim={trim}
                    driveType={driveType}
                    year={year}></TranslateHTML>
                )}
              </>
            ) : (
              <>
                {config ? (
                  <TranslateHTML
                    value="utility.bar.sub.title.with.config.no.trim.v3"
                    config={config}
                    driveType={driveType}
                    year={year}></TranslateHTML>
                ) : (
                  <TranslateHTML
                    value="utility.bar.sub.title.without.config.no.trim.v3"
                    driveType={driveType}
                    year={year}></TranslateHTML>
                )}
              </>
            )}
          </div>
        </h1>
        {!isRevealed && (
          <h1 className="utility-bar__title-content">
            <div
              className={`utility-bar__title utility-bar__title${
                modifier ? `--${modifier}` : ''
              }`}>
              <TranslateHTML
                value="utility.bar.title.v3"
                bodyStyle={bodyStyle}
              />
            </div>
            <div
              className={combineClassNames(
                'utility-bar__sub-title',
                modifier ? `utility-bar__sub-title--${modifier}` : ''
              )}>
              {year}
            </div>
          </h1>
        )}
      </>
    );
  }

  return (
    <>
      <h1
        className={`utility-bar__title-content--revealed ${
          isRevealed ? '' : 'utility-bar__title-content--hidden'
        }`}>
        <div
          className={combineClassNames(
            'utility-bar__title',
            modifier ? `utility-bar__title--${modifier}` : ''
          )}>
          <TranslateHTML
            value="utility.bar.title.v3"
            bodyStyle={bodyStyle}
            year={year}
          />
        </div>
        <div
          className={combineClassNames(
            'utility-bar__sub-title',
            modifier ? `utility-bar__sub-title--${modifier}` : ''
          )}>
          {isSelectionRevealed ? (
            <>
              {config && !isEvMode ? (
                <TranslateHTML
                  value="utility.bar.sub.title.with.config.v3"
                  config={config}
                  trim={trim}
                  driveType={driveType}></TranslateHTML>
              ) : (
                <TranslateHTML
                  value="utility.bar.sub.title.without.config.v3"
                  trim={trim}
                  driveType={driveType}></TranslateHTML>
              )}
            </>
          ) : (
            <>
              {config ? (
                <TranslateHTML
                  value="utility.bar.sub.title.with.config.no.trim.v3"
                  config={config}
                  driveType={driveType}></TranslateHTML>
              ) : (
                <TranslateHTML
                  value="utility.bar.sub.title.without.config.no.trim.v3"
                  driveType={driveType}></TranslateHTML>
              )}
            </>
          )}
        </div>
      </h1>
      {!isRevealed && (
        <h1 className="utility-bar__title-content">
          <div
            className={`utility-bar__title utility-bar__title${
              modifier ? `--${modifier}` : ''
            }`}>
            <TranslateHTML
              value="utility.bar.title.v3"
              bodyStyle={bodyStyle}
              year={year}
            />
          </div>
        </h1>
      )}
    </>
  );
};

const SavedBuildsCounter = ({
  isHeartActive,
  savedBuildCount,
  modifier = null,
}) => {
  const location = useLocation();

  return (
    <Link
      data-dtm="utility nav"
      to={`${location.pathname}/savedbuilds`}
      className={combineClassNames(
        'stat-button-link',
        'utility-bar__link',
        modifier ? `utility-bar__link--${modifier}` : '',
        isHeartActive ? 'utility-bar__link--active' : '',
        isHeartActive && modifier
          ? `utility-bar__link--${modifier}--active`
          : ''
      )}>
      {isHeartActive ? (
        <SvgIcon
          name="heart-active"
          className={combineClassNames(
            'utility-bar__icon--active',
            modifier ? `utility-bar__icon--${modifier}--active` : ''
          )}
        />
      ) : (
        <SvgIcon
          name="heart"
          className={combineClassNames(
            'utility-bar__icon',
            modifier ? `utility-bar__icon--${modifier}` : ''
          )}
          id="saved-icon"
        />
      )}
      <TranslateHTML
        className="utility-bar__text utility-bar__text--save-icon"
        value="saved.build.count.v3"
        count={savedBuildCount}></TranslateHTML>
    </Link>
  );
};

const UtilityBar = ({
  bodyStyle,
  year,
  driveType,
  config,
  trim,
  isSelectionRevealed,
  savedBuildCount,
  isBodyTypeRevealed,
}) => {
  const [isHeartActive, setIsHeartActive] = useState(false);
  const { isEvMode } = useModelInfo();
  const isRevealed = isEvMode
    ? trim && isSelectionRevealed
    : (trim || config) && (isBodyTypeRevealed || isSelectionRevealed);

  useEffect(() => {
    setIsHeartActive(savedBuildCount > 0);
  }, [savedBuildCount]);

  return (
    <header className="utility-bar-container">
      <div style={{ display: 'flex' }}>
        <VehicleUtilityInfo
          config={config}
          trim={trim}
          driveType={driveType}
          bodyStyle={bodyStyle}
          year={year}
          isRevealed={isRevealed}
          isSelectionRevealed={isSelectionRevealed}
        />
      </div>
      <SavedBuildsCounter
        isHeartActive={isHeartActive}
        savedBuildCount={savedBuildCount}
      />
    </header>
  );
};

export { VehicleUtilityInfo, SavedBuildsCounter };

export default UtilityBar;
